import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiUrl } from "../../config/api";
import ConfirmDialog from "../globalcontrols/confirmdialog/ConfirmDialog";
import AnimatedTabPanel from "./AnimatedTabPanel";
import "./tableeCarousel.css";

const defaultSlide = {
  id: "",
  sectionName: "",
  eyebrow: "",
  title: "",
  subtitle: "",
  ctaLabel: "",
  ctaPath: "",
  imageUrl: "/tablee_hero.jpg",
  heroImagePositionX: 50,
  heroImagePositionY: 50,
  heroImageZoom: 1,
  composerElements: [],
  sortOrder: 1,
  isActive: true,
};

const defaultHomeSettings = {
  logoUrl: "/tablee_logo.png",
  heroImageUrl: "/tablee_hero.jpg",
  heroImagePositionX: 50,
  heroImagePositionY: 50,
  heroImageZoom: 1,
  eyebrow: "",
  title: "",
  subtitle: "Insumos ricos, practicos y seleccionados para cocinar, vender y reunir personas alrededor de la mesa.",
  ctaLabel: "Comprar ahora",
  ctaPath: "/catalogo",
  slideIntervalSeconds: 6.5,
  slideTransition: "fade",
  slideTransitionSeconds: 0.5,
  isLogoVisible: true,
  isActive: true,
};

const transitionOptions = [
  { value: "fade", label: "Fundido" },
  { value: "slide", label: "Deslizar" },
  { value: "zoom", label: "Zoom suave" },
  { value: "none", label: "Sin transición" },
];

const defaultComposerElements = [
  { id: "logo", type: "logo", source: "logo", x: 8, y: 16, width: 28, height: 44, fontSize: 18, opacity: 1, zIndex: 2 },
  { id: "subtitle", type: "text", source: "subtitle", x: 8, y: 62, width: 44, height: 14, fontSize: 22, opacity: 1, zIndex: 3, color: "#fff8e6" },
  { id: "button", type: "button", source: "ctaLabel", x: 8, y: 80, width: 20, height: 10, fontSize: 14, opacity: 1, zIndex: 4, linkType: "view", linkPath: "/catalogo" },
];

const COMPOSER_DESIGN_WIDTH = 1080;

const tableeViewOptions = [
  { label: "Inicio", path: "/inicio" },
  { label: "Catalogo", path: "/catalogo" },
  { label: "Carro", path: "/carro" },
  { label: "Checkout", path: "/checkout" },
  { label: "Registro", path: "/registro" },
];

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("No se pudo leer la imagen."));
    reader.readAsDataURL(file);
  });
}

async function uploadTableeImage(file) {
  if (!file?.type?.startsWith("image/")) {
    throw new Error("Selecciona un archivo de imagen.");
  }
  if (file.size > 6 * 1024 * 1024) {
    throw new Error("La imagen debe pesar menos de 6 MB.");
  }

  const response = await fetch(apiUrl("/tablee/product-images"), {
    method: "POST",
    headers: getSessionHeaders(),
    body: JSON.stringify({
      filename: file.name,
      mimeType: file.type,
      dataUrl: await readFileAsDataUrl(file),
    }),
  });
  const data = await response.json();
  if (!response.ok || !data.success || !data.imageUrl) {
    throw new Error(data.message || "No se pudo subir la imagen.");
  }
  return data.imageUrl;
}

function getHeroImageTransform(settings) {
  const positionX = Number(settings?.heroImagePositionX ?? 50);
  const positionY = Number(settings?.heroImagePositionY ?? 50);
  const zoom = Number(settings?.heroImageZoom ?? 1);
  return `translate(${(positionX - 50) * 0.42}%, ${(positionY - 50) * 0.42}%) scale(${zoom})`;
}

function normalizeTransitionSeconds(value, fallback = 0.5) {
  const parsed = Number(value);
  const safeValue = Number.isFinite(parsed) ? parsed : fallback;
  return Number(Math.min(3, Math.max(0.1, safeValue)).toFixed(1));
}

function normalizeIntervalSeconds(value, fallback = 6.5) {
  const parsed = Number(value);
  const safeValue = Number.isFinite(parsed) ? parsed : fallback;
  return Number(Math.min(60, Math.max(2, safeValue)).toFixed(1));
}

function createComposerElement(type, options = {}) {
  const id = `${type}-${Date.now()}`;
  if (type === "logo") return { id, type: "logo", source: "logo", x: 10, y: 14, width: 26, height: 41, fontSize: 16, opacity: 1, zIndex: 5 };
  if (type === "image") return { id, type: "image", imageUrl: options.imageUrl || "", x: 58, y: 18, width: 24, height: 24, fontSize: 16, opacity: 1, zIndex: 5 };
  if (type === "button") return { id, type: "button", source: "ctaLabel", x: 10, y: 78, width: 20, height: 10, fontSize: 14, opacity: 1, zIndex: 5, linkType: "view", linkPath: "/catalogo" };
  if (type === "subtitle") return { id, type: "text", source: "subtitle", x: 10, y: 60, width: 42, height: 14, fontSize: 22, opacity: 1, zIndex: 5, color: "#fff8e6" };
  if (type === "shape-circle") return { id, type: "shape", shape: "circle", x: 58, y: 24, width: 16, height: 16, opacity: 0.72, zIndex: 1, background: "rgba(180, 154, 74, 0.42)" };
  if (type === "shape-rectangle") return { id, type: "shape", shape: "rectangle", x: 56, y: 58, width: 24, height: 12, opacity: 0.72, zIndex: 1, background: "rgba(255, 248, 230, 0.18)" };
  return { id, type: "text", text: "Nuevo texto", x: 10, y: 36, width: 30, height: 10, fontSize: 22, opacity: 1, zIndex: 5, color: "#fff8e6" };
}

function getElementText(element, draft) {
  if (element.source === "eyebrow") return draft.eyebrow || "Texto superior";
  if (element.source === "title") return draft.title || "Titulo de la sección";
  if (element.source === "subtitle") return draft.subtitle || "Bajada de la sección";
  if (element.source === "ctaLabel") return draft.ctaLabel || "Comprar ahora";
  return element.text || "Nuevo texto";
}

function getButtonLinkPath(element, draft) {
  return element?.linkPath || draft?.ctaPath || "/catalogo";
}

function renderComposerElementContent(element, draft) {
  if (element.type === "logo") return <img src="/images/tablee_logo.png" alt="" />;
  if (element.type === "image") return <img src={element.imageUrl || "/images/tablee_logo.png"} alt="" />;
  if (element.type === "text") return <span>{getElementText(element, draft)}</span>;
  if (element.type === "button") return <button type="button">{getElementText(element, draft)}</button>;
  return null;
}

function TableeHome() {
  const imageDragRef = useRef(null);
  const composerDragRef = useRef(null);
  const composerResizeRef = useRef(null);
  const composerCanvasRef = useRef(null);
  const composerElementsRef = useRef([]);
  const composerImageInputRef = useRef(null);
  const [slides, setSlides] = useState([]);
  const [homeSettings, setHomeSettings] = useState(defaultHomeSettings);
  const [settingsDraft, setSettingsDraft] = useState(defaultHomeSettings);
  const [imageDraft, setImageDraft] = useState(defaultSlide);
  const [selectedComposerElementId, setSelectedComposerElementId] = useState("");
  const [editingComposerElementId, setEditingComposerElementId] = useState("");
  const [composerContextMenu, setComposerContextMenu] = useState(null);
  const [composerHistory, setComposerHistory] = useState([]);
  const [composerRedoHistory, setComposerRedoHistory] = useState([]);
  const [composerPreviewScale, setComposerPreviewScale] = useState(1);
  const [isImageDragging, setIsImageDragging] = useState(false);
  const [isComposerDragging, setIsComposerDragging] = useState(false);
  const [isComposerResizing, setIsComposerResizing] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [sectionModalTab, setSectionModalTab] = useState("preview");
  const [productOptions, setProductOptions] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkPickerType, setLinkPickerType] = useState("section");
  const [isLinkDropdownOpen, setIsLinkDropdownOpen] = useState(false);
  const [isMediaDropdownOpen, setIsMediaDropdownOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [, setMessage] = useState("");

  async function loadSettings() {
    try {
      const response = await fetch(apiUrl("/tablee/home"), { headers: getSessionHeaders() });
      const data = await response.json();
      if (data.success) {
        setHomeSettings({ ...defaultHomeSettings, ...(data.settings || {}) });
        setSlides(data.slides || []);
      } else {
        setMessage(data.message || "No se pudo cargar la portada.");
      }
    } catch {
      setMessage("No se pudo conectar con la API.");
    }
  }

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    composerElementsRef.current = imageDraft.composerElements || [];
  }, [imageDraft.composerElements]);

  useEffect(() => {
    if (!isImageModalOpen) return;
    loadProductOptions();
  }, [isImageModalOpen]);

  useEffect(() => {
    if (!isImageModalOpen || sectionModalTab !== "preview") return undefined;
    const canvas = composerCanvasRef.current;
    if (!canvas) return undefined;

    function updateComposerPreviewScale() {
      const { width } = canvas.getBoundingClientRect();
      const nextScale = Math.min(1, Math.max(0.35, width / COMPOSER_DESIGN_WIDTH));
      setComposerPreviewScale((current) => (Math.abs(current - nextScale) > 0.01 ? nextScale : current));
    }

    updateComposerPreviewScale();
    const observer = new ResizeObserver(updateComposerPreviewScale);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [isImageModalOpen, sectionModalTab]);

  async function loadProductOptions() {
    setProductsLoading(true);
    try {
      const response = await fetch(apiUrl("/tablee/products"), { headers: getSessionHeaders() });
      const data = await response.json();
      if (data.success && Array.isArray(data.products)) {
        setProductOptions(data.products.map((product) => ({
          id: String(product.id),
          name: product.name,
          unitLabel: product.unitLabel || product.unit || "",
        })));
      }
    } catch {
      setProductOptions([]);
    } finally {
      setProductsLoading(false);
    }
  }

  function openImageModal(slide = null) {
    const initialSlide = {
      ...defaultSlide,
      ...(slide || {}),
      sortOrder: slide?.sortOrder || slides.length + 1,
    };
    const composerElements = Array.isArray(initialSlide.composerElements) && initialSlide.composerElements.length
      ? initialSlide.composerElements
      : defaultComposerElements;
    setImageDraft({
      ...initialSlide,
      composerElements,
    });
    setSelectedComposerElementId(composerElements[0]?.id || "");
    setEditingComposerElementId("");
    setComposerContextMenu(null);
    setComposerHistory([]);
    setComposerRedoHistory([]);
    setIsLinkModalOpen(false);
    setIsLinkDropdownOpen(false);
    setSectionModalTab("preview");
    setIsImageModalOpen(true);
  }

  function openSettingsModal() {
    setSettingsDraft({
      ...defaultHomeSettings,
      ...homeSettings,
      slideIntervalSeconds: normalizeIntervalSeconds(homeSettings.slideIntervalSeconds).toFixed(1),
      slideTransitionSeconds: normalizeTransitionSeconds(homeSettings.slideTransitionSeconds).toFixed(1),
    });
    setMessage("");
    setIsSettingsModalOpen(true);
  }

  function updateSettingsDraft(field, value) {
    setSettingsDraft((current) => ({ ...current, [field]: value }));
  }

  async function saveHomeSettings(event) {
    event.preventDefault();
    setMessage("");
    const payload = {
      ...defaultHomeSettings,
      ...homeSettings,
      ...settingsDraft,
      slideIntervalSeconds: normalizeIntervalSeconds(settingsDraft.slideIntervalSeconds),
      slideTransition: transitionOptions.some((option) => option.value === settingsDraft.slideTransition) ? settingsDraft.slideTransition : "fade",
      slideTransitionSeconds: normalizeTransitionSeconds(settingsDraft.slideTransitionSeconds),
    };

    try {
      const response = await fetch(apiUrl("/tablee/home"), {
        method: "PUT",
        headers: getSessionHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo guardar la configuracion.");
        return;
      }
      setHomeSettings({ ...defaultHomeSettings, ...payload, ...(data.settings || {}) });
      setIsSettingsModalOpen(false);
      setMessage("Configuracion guardada.");
    } catch {
      setMessage("No se pudo conectar con la API.");
    }
  }

  function updateImageDraft(field, value) {
    setImageDraft((current) => ({ ...current, [field]: value }));
  }

  function changeSectionOrder(delta) {
    setImageDraft((current) => ({
      ...current,
      sortOrder: Math.max(1, Number(current.sortOrder || 1) + delta),
    }));
  }

  const pushComposerHistory = useCallback(() => {
    setComposerHistory((current) => [
      ...(current.length > 24 ? current.slice(1) : current),
      JSON.stringify(composerElementsRef.current || []),
    ]);
    setComposerRedoHistory([]);
  }, []);

  function undoComposerChange() {
    setComposerHistory((current) => {
      const previous = current[current.length - 1];
      if (!previous) return current;
      setComposerRedoHistory((redoCurrent) => [
        ...(redoCurrent.length > 24 ? redoCurrent.slice(1) : redoCurrent),
        JSON.stringify(composerElementsRef.current || []),
      ]);
      const parsed = JSON.parse(previous);
      setImageDraft((draft) => ({ ...draft, composerElements: parsed }));
      setSelectedComposerElementId(parsed[0]?.id || "");
      setEditingComposerElementId("");
      return current.slice(0, -1);
    });
  }

  function redoComposerChange() {
    setComposerRedoHistory((current) => {
      const next = current[current.length - 1];
      if (!next) return current;
      setComposerHistory((historyCurrent) => [
        ...(historyCurrent.length > 24 ? historyCurrent.slice(1) : historyCurrent),
        JSON.stringify(composerElementsRef.current || []),
      ]);
      const parsed = JSON.parse(next);
      setImageDraft((draft) => ({ ...draft, composerElements: parsed }));
      setSelectedComposerElementId(parsed[0]?.id || "");
      setEditingComposerElementId("");
      return current.slice(0, -1);
    });
  }

  function updateComposerElement(elementId, updater) {
    setImageDraft((current) => ({
      ...current,
      composerElements: (current.composerElements || []).map((element) => (
        element.id === elementId ? { ...element, ...(typeof updater === "function" ? updater(element) : updater) } : element
      )),
    }));
  }

  function addComposerElement(type, options = {}) {
    pushComposerHistory();
    const element = createComposerElement(type, options);
    setImageDraft((current) => ({
      ...current,
      composerElements: [
        ...(current.composerElements || []),
        { ...element, zIndex: Math.max(1, ...(current.composerElements || []).map((entry) => Number(entry.zIndex || 1))) + 1 },
      ],
    }));
    setSelectedComposerElementId(element.id);
    setIsMediaDropdownOpen(false);
  }

  async function handleComposerImageFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setMessage("");
    try {
      addComposerElement("image", { imageUrl: await uploadTableeImage(file) });
    } catch (err) {
      setMessage(err.message || "No se pudo subir la imagen.");
    }
  }

  const deleteComposerElement = useCallback((elementId) => {
    if (!elementId) return;
    pushComposerHistory();
    setImageDraft((current) => {
      const nextElements = (current.composerElements || []).filter((element) => element.id !== elementId);
      setSelectedComposerElementId(nextElements[0]?.id || "");
      return { ...current, composerElements: nextElements };
    });
    setEditingComposerElementId("");
    setComposerContextMenu(null);
    setIsLinkModalOpen(false);
  }, [pushComposerHistory]);

  const getComposerElementLabel = useCallback((elementId) => {
    const element = (imageDraft.composerElements || []).find((entry) => entry.id === elementId);
    if (!element) return "este elemento";
    if (element.type === "logo") return "logo";
    if (element.type === "image") return "imagen";
    if (element.type === "button") return "boton";
    if (element.type === "text") return "texto";
    return "elemento";
  }, [imageDraft.composerElements]);

  const requestDeleteComposerElement = useCallback((elementId) => {
    if (!elementId) return;
    setConfirmDialog({
      title: "Eliminar elemento",
      message: `Eliminar ${getComposerElementLabel(elementId)} de la composicion?`,
      confirmLabel: "Eliminar",
      onConfirm: () => {
        deleteComposerElement(elementId);
        setConfirmDialog(null);
      },
    });
  }, [deleteComposerElement, getComposerElementLabel]);

  function deleteSelectedComposerElement() {
    requestDeleteComposerElement(selectedComposerElementId);
  }

  useEffect(() => {
    function handleComposerKeyDown(event) {
      if (!isImageModalOpen || sectionModalTab !== "preview" || !selectedComposerElementId || editingComposerElementId || isLinkModalOpen) return;
      const target = event.target;
      const isTyping = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
      if (isTyping) return;
      if (!["Backspace", "Delete"].includes(event.key)) return;
      event.preventDefault();
      requestDeleteComposerElement(selectedComposerElementId);
    }

    window.addEventListener("keydown", handleComposerKeyDown);
    return () => window.removeEventListener("keydown", handleComposerKeyDown);
  }, [isImageModalOpen, sectionModalTab, selectedComposerElementId, editingComposerElementId, isLinkModalOpen, requestDeleteComposerElement]);

  function moveSelectedComposerLayer(direction) {
    if (!selectedComposerElementId) return;
    pushComposerHistory();
    updateComposerElement(selectedComposerElementId, (element) => ({
      zIndex: Math.min(99, Math.max(1, Number(element.zIndex || 1) + direction)),
    }));
  }

  function updateSelectedComposerStyle(updater) {
    if (!selectedComposerElementId) return;
    pushComposerHistory();
    updateComposerElement(selectedComposerElementId, updater);
  }

  function beginComposerTextEdit(elementId) {
    pushComposerHistory();
    setSelectedComposerElementId(elementId);
    setEditingComposerElementId(elementId);
    setComposerContextMenu(null);
  }

  function updateComposerText(element, value) {
    updateComposerElement(element.id, {
      source: "",
      text: value,
    });
  }

  function updateSelectedButtonLink(updates) {
    if (!selectedComposerElementId) return;
    pushComposerHistory();
    updateComposerElement(selectedComposerElementId, updates);
  }

  function openSelectedButtonLinkModal(elementId) {
    const element = (imageDraft.composerElements || []).find((entry) => entry.id === elementId);
    setSelectedComposerElementId(elementId);
    setEditingComposerElementId("");
    setComposerContextMenu(null);
    setLinkPickerType(element?.linkType === "product" ? "product" : "section");
    setIsLinkDropdownOpen(false);
    setIsLinkModalOpen(true);
  }

  function chooseSelectedButtonLink(updates) {
    updateSelectedButtonLink(updates);
    setIsLinkDropdownOpen(false);
    setIsLinkModalOpen(false);
  }

  function startComposerDrag(event, element) {
    if (event.button != null && event.button !== 0) return;
    if (editingComposerElementId === element.id) return;
    pushComposerHistory();
    const bounds = event.currentTarget.parentElement.getBoundingClientRect();
    composerDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      width: bounds.width || 1,
      height: bounds.height || 1,
      x: Number(element.x || 0),
      y: Number(element.y || 0),
      elementId: element.id,
    };
    setSelectedComposerElementId(element.id);
    setComposerContextMenu(null);
    setIsComposerDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function dragComposerElement(event) {
    const drag = composerDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const deltaX = ((event.clientX - drag.startX) / drag.width) * 100;
    const deltaY = ((event.clientY - drag.startY) / drag.height) * 100;
    updateComposerElement(drag.elementId, {
      x: Math.min(100, Math.max(0, drag.x + deltaX)),
      y: Math.min(100, Math.max(0, drag.y + deltaY)),
    });
  }

  function stopComposerDrag(event) {
    if (composerDragRef.current?.pointerId === event.pointerId) {
      composerDragRef.current = null;
      setIsComposerDragging(false);
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  }

  function startComposerResize(event, element, anchor) {
    event.preventDefault();
    event.stopPropagation();
    const canvas = event.currentTarget.closest(".tablee-home-admin__composer-canvas");
    const bounds = canvas?.getBoundingClientRect();
    if (!bounds) return;
    pushComposerHistory();
    composerResizeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      width: bounds.width || 1,
      height: bounds.height || 1,
      elementId: element.id,
      anchor,
      x: Number(element.x || 0),
      y: Number(element.y || 0),
      elementWidth: Number(element.width || 20),
      elementHeight: Number(element.height || 10),
    };
    setSelectedComposerElementId(element.id);
    setComposerContextMenu(null);
    setIsComposerResizing(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function dragComposerResize(event) {
    const resize = composerResizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;
    const deltaX = ((event.clientX - resize.startX) / resize.width) * 100;
    const deltaY = ((event.clientY - resize.startY) / resize.height) * 100;
    const next = {
      x: resize.x,
      y: resize.y,
      width: resize.elementWidth,
      height: resize.elementHeight,
    };

    if (resize.anchor.includes("e")) next.width = resize.elementWidth + deltaX;
    if (resize.anchor.includes("s")) next.height = resize.elementHeight + deltaY;
    if (resize.anchor.includes("w")) {
      next.x = resize.x + deltaX;
      next.width = resize.elementWidth - deltaX;
    }
    if (resize.anchor.includes("n")) {
      next.y = resize.y + deltaY;
      next.height = resize.elementHeight - deltaY;
    }

    next.width = Math.min(100, Math.max(4, next.width));
    next.height = Math.min(100, Math.max(4, next.height));
    next.x = Math.min(100 - next.width, Math.max(0, next.x));
    next.y = Math.min(100 - next.height, Math.max(0, next.y));
    updateComposerElement(resize.elementId, next);
  }

  function stopComposerResize(event) {
    if (composerResizeRef.current?.pointerId === event.pointerId) {
      composerResizeRef.current = null;
      setIsComposerResizing(false);
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  }

  function clampPercent(value) {
    return Math.min(100, Math.max(0, value));
  }

  function startImageDrag(event) {
    if (event.button != null && event.button !== 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    imageDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      width: bounds.width || 1,
      height: bounds.height || 1,
      positionX: Number(imageDraft.heroImagePositionX ?? 50),
      positionY: Number(imageDraft.heroImagePositionY ?? 50),
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsImageDragging(true);
  }

  function dragImage(event) {
    const drag = imageDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const deltaX = ((event.clientX - drag.startX) / drag.width) * 100;
    const deltaY = ((event.clientY - drag.startY) / drag.height) * 100;
    setImageDraft((current) => ({
      ...current,
      heroImagePositionX: clampPercent(drag.positionX + deltaX),
      heroImagePositionY: clampPercent(drag.positionY + deltaY),
    }));
  }

  function stopImageDrag(event) {
    if (imageDragRef.current?.pointerId === event.pointerId) {
      imageDragRef.current = null;
      setIsImageDragging(false);
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  }

  async function handleImageFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setMessage("");
    try {
      updateImageDraft("imageUrl", await uploadTableeImage(file));
    } catch (err) {
      setMessage(err.message || "No se pudo subir la imagen.");
    }
  }

  async function saveSlide() {
    setMessage("");
    const method = imageDraft.id ? "PUT" : "POST";
    const path = imageDraft.id ? `/tablee/home/slides/${imageDraft.id}` : "/tablee/home/slides";

    try {
      const response = await fetch(apiUrl(path), {
        method,
        headers: getSessionHeaders(),
        body: JSON.stringify(imageDraft),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo guardar la sección.");
        return;
      }
      setIsImageModalOpen(false);
      setMessage("Sección guardada.");
      await loadSettings();
    } catch {
      setMessage("No se pudo conectar con la API.");
    }
  }

  async function toggleSlide(slide) {
    setSlides((current) => current.map((entry) => entry.id === slide.id ? { ...entry, isActive: !slide.isActive } : entry));
    try {
      const response = await fetch(apiUrl(`/tablee/home/slides/${slide.id}/active`), {
        method: "PATCH",
        headers: getSessionHeaders(),
        body: JSON.stringify({ isActive: !slide.isActive }),
      });
      const data = await response.json();
      if (!data.success) await loadSettings();
    } catch {
      await loadSettings();
    }
  }

  function deleteSlide(slide) {
    setConfirmDialog({
      title: "Eliminar sección",
      message: `Eliminar "${slide.sectionName || slide.title || `Sección ${slide.sortOrder}`}" de las secciones rotativas?`,
      confirmLabel: "Eliminar",
      onConfirm: () => confirmDeleteSlide(slide),
    });
  }

  async function confirmDeleteSlide(slide) {
    setConfirmDialog(null);
    setMessage("");
    try {
      const response = await fetch(apiUrl(`/tablee/home/slides/${slide.id}`), {
        method: "DELETE",
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo eliminar la sección.");
        return;
      }
      setMessage("Sección eliminada.");
      await loadSettings();
    } catch {
      setMessage("No se pudo conectar con la API.");
    }
  }

  const selectedComposerElement = (imageDraft.composerElements || []).find((element) => element.id === selectedComposerElementId) || null;
  const selectedCanEditText = selectedComposerElement && ["text", "button"].includes(selectedComposerElement.type);
  const selectedIsButton = selectedComposerElement?.type === "button";
  const selectedButtonPath = getButtonLinkPath(selectedComposerElement, imageDraft);
  const linkDropdownOptions = linkPickerType === "product"
    ? productOptions.map((product) => ({ label: product.name, path: `/producto/${product.id}`, linkedProductId: product.id }))
    : tableeViewOptions.map((option) => ({ label: option.label, path: option.path, linkedProductId: "" }));
  const selectedLinkLabel = linkDropdownOptions.find((option) => option.path === selectedButtonPath)?.label
    || (linkPickerType === "product" ? "Selecciona un producto" : selectedButtonPath);

  return (
    <div className="tablee-carousel tablee-home-admin">
      <header className="tablee-carousel__header">
        <img src="/images/tablee_logo.png" alt="Tablee" />
        <div>
          <span>Tablee</span>
          <h1>Inicio Tablee</h1>
          <p>Administra las secciones rotativas visibles en la portada de www.tablee.cl.</p>
        </div>
      </header>

      <section className="tablee-carousel__display tablee-home-admin__slides">
        <div className="tablee-carousel__display-head">
          <div>
            <span>Inicio</span>
            <h2>Secciones rotativas</h2>
          </div>
          <div className="tablee-home-admin__head-actions">
            <button type="button" className="tablee-home-admin__settings-button" onClick={openSettingsModal} aria-label="Configurar portada">
              <span className="material-symbols-outlined">settings</span>
            </button>
            <button type="button" onClick={() => openImageModal()}>Agregar sección</button>
          </div>
        </div>
        <div className="tablee-home-admin__slides-table" aria-hidden="true">
          <span>Sección</span>
          <span>Orden</span>
          <span>Estado</span>
          <span>Acciones</span>
        </div>
        <div className="tablee-home-admin__slides-list">
          {slides.length ? slides.map((slide) => (
            <article key={slide.id} className={!slide.isActive ? "is-hidden" : ""}>
              <div className="tablee-home-admin__section-cell">
                <div className="tablee-home-admin__section-preview" aria-hidden="true">
                  <img
                    className="tablee-home-admin__section-preview-image"
                    src={slide.imageUrl || "/tablee_hero.jpg"}
                    alt=""
                    style={{ transform: getHeroImageTransform(slide) }}
                  />
                  <div className="tablee-home-admin__section-preview-overlay" />
                  {(slide.composerElements || []).map((element) => {
                    const style = {
                      left: `${element.x}%`,
                      top: `${element.y}%`,
                      width: `${element.width}%`,
                      height: `${element.height}%`,
                      zIndex: element.zIndex || 1,
                      opacity: element.opacity ?? 1,
                      fontSize: `${Math.max(5, Number(element.fontSize || 18) * 0.22)}px`,
                      color: element.color || "#fff8e6",
                      fontWeight: element.fontWeight || undefined,
                      fontStyle: element.fontStyle || undefined,
                      background: element.background || undefined,
                    };
                    return (
                      <div
                        key={element.id}
                        className={`tablee-home-admin__section-preview-element is-${element.type} ${element.shape ? `is-${element.shape}` : ""}`}
                        style={style}
                      >
                        {renderComposerElementContent(element, slide)}
                      </div>
                    );
                  })}
                </div>
                <div>
                  <strong>{slide.sectionName || slide.title || `Sección ${slide.sortOrder}`}</strong>
                  <small>{slide.subtitle || "Sin bajada configurada."}</small>
                </div>
              </div>
              <strong>{slide.sortOrder}</strong>
              <label className="tablee-carousel__switch">
                <input type="checkbox" checked={slide.isActive} onChange={() => toggleSlide(slide)} />
                <span />
                <strong>{slide.isActive ? "Activo" : "Oculto"}</strong>
              </label>
              <div className="tablee-home-admin__row-actions">
                <button type="button" onClick={() => openImageModal(slide)} aria-label="Editar sección">
                  <span className="material-symbols-outlined">edit</span>
                </button>
                <button type="button" className="tablee-carousel__danger" onClick={() => deleteSlide(slide)} aria-label="Eliminar sección">
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </div>
            </article>
          )) : (
            <p className="tablee-carousel__empty">No hay secciones de inicio configuradas.</p>
          )}
        </div>
      </section>

      {isImageModalOpen ? (
        <div className="tablee-carousel__modal-backdrop" role="presentation" onMouseDown={() => setIsImageModalOpen(false)}>
          <div className="tablee-carousel__modal tablee-home-admin__image-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <div className="tablee-carousel__modal-head">
              <div>
                <span>Sección de inicio</span>
                <h2>{imageDraft.id ? "Editar sección" : "Agregar sección"}</h2>
              </div>
              <button type="button" className="tablee-carousel__icon-button" onClick={() => setIsImageModalOpen(false)} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="tablee-home-admin__section-tabs" role="tablist" aria-label="Secciones del modal">
              <button
                type="button"
                className={sectionModalTab === "image" ? "is-active" : ""}
                onClick={() => setSectionModalTab("image")}
                role="tab"
                aria-selected={sectionModalTab === "image"}
              >
                Imagen
              </button>
              <button
                type="button"
                className={sectionModalTab === "preview" ? "is-active" : ""}
                onClick={() => setSectionModalTab("preview")}
                role="tab"
                aria-selected={sectionModalTab === "preview"}
              >
                Editor
              </button>
            </div>

            <AnimatedTabPanel
              tabKey={sectionModalTab}
              contentClassName={`tablee-home-admin__section-editor ${sectionModalTab === "image" ? "is-image-tab" : ""} ${sectionModalTab === "preview" ? "is-preview-tab" : ""}`}
            >
              {sectionModalTab === "image" ? (
              <div className="tablee-home-admin__section-block tablee-home-admin__image-tab" role="tabpanel">
                <div className="tablee-home-admin__section-block-head">
                  <span>Imagen de la sección</span>
                  <strong>Arrastra la imagen para encuadrarla</strong>
                </div>
                <div className="tablee-home-admin__image-tools">
                  <span className="tablee-home-admin__file-picker">
                    <input type="file" accept="image/*" onChange={handleImageFile} />
                    <span className="tablee-home-admin__file-button">
                      <span className="material-symbols-outlined">upload</span>
                      Elegir imagen
                    </span>
                  </span>
                </div>
                <div className="tablee-home-admin__image-main">
                  <div
                    className={`tablee-home-admin__image-stage ${isImageDragging ? "is-dragging" : ""}`}
                    onPointerDown={startImageDrag}
                    onPointerMove={dragImage}
                    onPointerUp={stopImageDrag}
                    onPointerCancel={stopImageDrag}
                  >
                    <img
                      src={imageDraft.imageUrl || "/tablee_hero.jpg"}
                      alt=""
                      style={{ transform: getHeroImageTransform(imageDraft) }}
                    />
                    <div />
                  </div>
                  <label className="tablee-carousel__field tablee-home-admin__range-field tablee-home-admin__zoom-field">
                    <span>Zoom</span>
                    <input type="range" min="1" max="2.5" step="0.05" value={imageDraft.heroImageZoom ?? 1} onChange={(event) => updateImageDraft("heroImageZoom", Number(event.target.value))} />
                  </label>
                </div>
              </div>
              ) : null}

              {sectionModalTab === "preview" ? (
              <div className="tablee-home-admin__section-block tablee-home-admin__composer" role="tabpanel">
                <div className="tablee-home-admin__composer-toolbar" aria-label="Herramientas del editor">
                  <div>
                    <button type="button" onClick={undoComposerChange} disabled={!composerHistory.length} title="Deshacer"><span className="material-symbols-outlined">undo</span></button>
                    <button type="button" onClick={redoComposerChange} disabled={!composerRedoHistory.length} title="Rehacer"><span className="material-symbols-outlined">redo</span></button>
                    <button type="button" onClick={() => addComposerElement("text")} title="Agregar texto"><span className="material-symbols-outlined">title</span></button>
                    <button type="button" onClick={() => addComposerElement("subtitle")} title="Agregar bajada"><span className="material-symbols-outlined">subject</span></button>
                    <div className={`tablee-home-admin__media-dropdown ${isMediaDropdownOpen ? "is-open" : ""}`}>
                      <button type="button" onClick={() => setIsMediaDropdownOpen((current) => !current)} title="Agregar imagen o logo">
                        <span className="material-symbols-outlined">add_photo_alternate</span>
                      </button>
                      {isMediaDropdownOpen ? (
                        <div className="tablee-home-admin__media-menu">
                          <button type="button" onClick={() => addComposerElement("logo")}>
                            <span className="material-symbols-outlined">workspace_premium</span>
                            Agregar logo
                          </button>
                          <button type="button" onClick={() => composerImageInputRef.current?.click()}>
                            <span className="material-symbols-outlined">image</span>
                            Agregar imagen
                          </button>
                        </div>
                      ) : null}
                      <input ref={composerImageInputRef} type="file" accept="image/*" onChange={handleComposerImageFile} />
                    </div>
                    <button type="button" onClick={() => addComposerElement("button")} title="Agregar boton"><span className="material-symbols-outlined">smart_button</span></button>
                    <button type="button" onClick={() => addComposerElement("shape-rectangle")} title="Agregar rectangulo"><span className="material-symbols-outlined">rectangle</span></button>
                    <button type="button" onClick={() => addComposerElement("shape-circle")} title="Agregar circulo"><span className="material-symbols-outlined">circle</span></button>
                  </div>
                  <div>
                    <button type="button" onClick={() => updateSelectedComposerStyle((element) => ({ fontSize: Math.max(8, Number(element.fontSize || 18) - 2) }))} disabled={!selectedCanEditText} title="Reducir texto"><span className="material-symbols-outlined">text_decrease</span></button>
                    <button type="button" onClick={() => updateSelectedComposerStyle((element) => ({ fontSize: Math.min(72, Number(element.fontSize || 18) + 2) }))} disabled={!selectedCanEditText} title="Aumentar texto"><span className="material-symbols-outlined">text_increase</span></button>
                    <label className="tablee-home-admin__composer-color" title="Color de texto">
                      <span className="material-symbols-outlined">format_color_text</span>
                      <input
                        type="color"
                        value={selectedComposerElement?.color || "#fff8e6"}
                        disabled={!selectedCanEditText}
                        onChange={(event) => updateSelectedComposerStyle({ color: event.target.value })}
                      />
                    </label>
                    <button type="button" onClick={() => updateSelectedComposerStyle((element) => ({ fontWeight: Number(element.fontWeight || 850) >= 850 ? 500 : 850 }))} disabled={!selectedCanEditText} title="Negrita"><span className="material-symbols-outlined">format_bold</span></button>
                    <button type="button" onClick={() => updateSelectedComposerStyle((element) => ({ fontStyle: element.fontStyle === "italic" ? "normal" : "italic" }))} disabled={!selectedCanEditText} title="Cursiva"><span className="material-symbols-outlined">format_italic</span></button>
                    <button type="button" onClick={() => moveSelectedComposerLayer(1)} disabled={!selectedComposerElementId} title="Enviar al frente"><span className="material-symbols-outlined">flip_to_front</span></button>
                    <button type="button" onClick={() => moveSelectedComposerLayer(-1)} disabled={!selectedComposerElementId} title="Enviar atras"><span className="material-symbols-outlined">flip_to_back</span></button>
                    <button type="button" className="tablee-carousel__danger" onClick={deleteSelectedComposerElement} disabled={!selectedComposerElementId} title="Eliminar"><span className="material-symbols-outlined">delete</span></button>
                  </div>
                </div>
                <div ref={composerCanvasRef} className="tablee-home-admin__composer-canvas" onPointerDown={() => { setComposerContextMenu(null); setIsMediaDropdownOpen(false); }}>
                  <img
                    className="tablee-home-admin__final-preview-image"
                    src={imageDraft.imageUrl || "/tablee_hero.jpg"}
                    alt=""
                    style={{ transform: getHeroImageTransform(imageDraft) }}
                  />
                  <div className="tablee-home-admin__final-preview-overlay" />
                  {(imageDraft.composerElements || []).map((element) => {
                    const style = {
                      left: `${element.x}%`,
                      top: `${element.y}%`,
                      width: `${element.width}%`,
                      height: `${element.height}%`,
                      zIndex: element.zIndex || 1,
                      opacity: element.opacity ?? 1,
                      fontSize: `${Math.max(5, Number(element.fontSize || 18) * composerPreviewScale)}px`,
                      color: element.color || "#fff8e6",
                      fontWeight: element.fontWeight || undefined,
                      fontStyle: element.fontStyle || undefined,
                      background: element.background || undefined,
                    };
                    const isSelected = selectedComposerElementId === element.id;
                    const className = `tablee-home-admin__composer-element is-${element.type} ${element.shape ? `is-${element.shape}` : ""} ${isSelected ? "is-selected" : ""} ${isComposerDragging && isSelected ? "is-dragging" : ""} ${isComposerResizing && isSelected ? "is-resizing" : ""}`;
                    return (
                      <div
                        key={element.id}
                        className={className}
                        style={style}
                        onPointerDown={(event) => startComposerDrag(event, element)}
                        onPointerMove={dragComposerElement}
                        onPointerUp={stopComposerDrag}
                        onPointerCancel={stopComposerDrag}
                        onContextMenu={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setSelectedComposerElementId(element.id);
                          setComposerContextMenu({ elementId: element.id, x: event.clientX, y: event.clientY });
                        }}
                        onDoubleClick={(event) => {
                          if (!["text", "button"].includes(element.type)) return;
                          event.stopPropagation();
                          beginComposerTextEdit(element.id);
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        {element.type === "logo" ? <img src="/images/tablee_logo.png" alt="" /> : null}
                        {element.type === "image" ? <img src={element.imageUrl || "/images/tablee_logo.png"} alt="" /> : null}
                        {element.type === "text" && editingComposerElementId !== element.id ? <span>{getElementText(element, imageDraft)}</span> : null}
                        {element.type === "text" && editingComposerElementId === element.id ? (
                          <textarea
                            value={getElementText(element, imageDraft)}
                            onPointerDown={(event) => event.stopPropagation()}
                            onDoubleClick={(event) => event.stopPropagation()}
                            onChange={(event) => updateComposerText(element, event.target.value)}
                            onBlur={() => setEditingComposerElementId("")}
                            autoFocus
                          />
                        ) : null}
                        {element.type === "button" && editingComposerElementId !== element.id ? <button type="button">{getElementText(element, imageDraft)}</button> : null}
                        {element.type === "button" && editingComposerElementId === element.id ? (
                          <input
                            value={getElementText(element, imageDraft)}
                            onPointerDown={(event) => event.stopPropagation()}
                            onDoubleClick={(event) => event.stopPropagation()}
                            onChange={(event) => updateComposerText(element, event.target.value)}
                            onBlur={() => setEditingComposerElementId("")}
                            autoFocus
                          />
                        ) : null}
                        {isSelected && editingComposerElementId !== element.id ? (
                          <>
                            {["nw", "n", "ne", "e", "se", "s", "sw", "w"].map((anchor) => (
                              <span
                                key={anchor}
                                className={`tablee-home-admin__composer-anchor is-${anchor}`}
                                onPointerDown={(event) => startComposerResize(event, element, anchor)}
                                onPointerMove={dragComposerResize}
                                onPointerUp={stopComposerResize}
                                onPointerCancel={stopComposerResize}
                                aria-hidden="true"
                              />
                            ))}
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                  {composerContextMenu ? (
                    <div
                      className="tablee-home-admin__composer-context-menu"
                      style={{ left: composerContextMenu.x, top: composerContextMenu.y }}
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      {["text", "button"].includes((imageDraft.composerElements || []).find((element) => element.id === composerContextMenu.elementId)?.type) ? (
                        <button type="button" onClick={() => beginComposerTextEdit(composerContextMenu.elementId)}>
                          <span className="material-symbols-outlined">edit</span>
                          Editar texto
                        </button>
                      ) : null}
                      {(imageDraft.composerElements || []).find((element) => element.id === composerContextMenu.elementId)?.type === "button" ? (
                        <button type="button" onClick={() => openSelectedButtonLinkModal(composerContextMenu.elementId)}>
                          <span className="material-symbols-outlined">link</span>
                          Set up link
                        </button>
                      ) : null}
                      <button type="button" className="is-danger" onClick={() => requestDeleteComposerElement(composerContextMenu.elementId)}>
                        <span className="material-symbols-outlined">delete</span>
                        Eliminar
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
              ) : null}

              <div className="tablee-home-admin__image-controls" />
            </AnimatedTabPanel>

            {isLinkModalOpen && selectedIsButton ? (
              <div className="tablee-home-admin__link-modal-backdrop" role="presentation" onMouseDown={() => setIsLinkModalOpen(false)}>
                <div className="tablee-home-admin__link-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
                  <div className="tablee-home-admin__link-modal-head">
                    <div>
                      <span>Destino del boton</span>
                      <h3>Set up link</h3>
                      <p>{selectedButtonPath}</p>
                    </div>
                    <button type="button" onClick={() => setIsLinkModalOpen(false)} aria-label="Cerrar">
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  </div>
                  <div className="tablee-home-admin__link-modal-body">
                    <div className="tablee-home-admin__link-type-toggle" role="group" aria-label="Tipo de enlace">
                      <button
                        type="button"
                        className={linkPickerType === "section" ? "is-active" : ""}
                        onClick={() => {
                          setLinkPickerType("section");
                          setIsLinkDropdownOpen(false);
                        }}
                      >
                        Section
                      </button>
                      <button
                        type="button"
                        className={linkPickerType === "product" ? "is-active" : ""}
                        onClick={() => {
                          setLinkPickerType("product");
                          setIsLinkDropdownOpen(false);
                        }}
                      >
                        Product
                      </button>
                      <span className={`tablee-home-admin__link-type-thumb is-${linkPickerType}`} />
                    </div>
                    <section>
                      <span>{linkPickerType === "product" ? (productsLoading ? "Cargando productos..." : "Producto") : "Sección"}</span>
                      <div className={`tablee-home-admin__link-dropdown ${isLinkDropdownOpen ? "is-open" : ""}`}>
                        <button
                          type="button"
                          className="tablee-home-admin__link-dropdown-trigger"
                          onClick={() => setIsLinkDropdownOpen((current) => !current)}
                        >
                          <strong>{selectedLinkLabel}</strong>
                          <span className="material-symbols-outlined">expand_more</span>
                        </button>
                        {isLinkDropdownOpen ? (
                          <div className="tablee-home-admin__link-dropdown-menu">
                            {linkDropdownOptions.map((option) => (
                              <button
                                key={`${linkPickerType}-${option.path}`}
                                type="button"
                                className={selectedButtonPath === option.path ? "is-active" : ""}
                                onClick={() => chooseSelectedButtonLink({
                                  linkType: linkPickerType === "product" ? "product" : "view",
                                  linkPath: option.path,
                                  linkedProductId: option.linkedProductId,
                                })}
                              >
                                {option.label}
                              </button>
                            ))}
                            {!productsLoading && !linkDropdownOptions.length ? <small>No hay opciones disponibles.</small> : null}
                          </div>
                        ) : null}
                      </div>
                    </section>
                  </div>
                  <div className="tablee-home-admin__link-modal-actions">
                    <button type="button" className="tablee-carousel__secondary" onClick={() => setIsLinkModalOpen(false)}>Cancelar</button>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="tablee-carousel__modal-actions tablee-home-admin__image-actions">
              <div className="tablee-home-admin__footer-controls">
                <div className="tablee-home-admin__order-control" aria-label="Orden de la sección">
                  <span>Orden</span>
                  <div>
                    <button type="button" onClick={() => changeSectionOrder(-1)} aria-label="Bajar orden">
                      <span className="material-symbols-outlined">remove</span>
                    </button>
                    <strong>{imageDraft.sortOrder || 1}</strong>
                    <button type="button" onClick={() => changeSectionOrder(1)} aria-label="Subir orden">
                      <span className="material-symbols-outlined">add</span>
                    </button>
                  </div>
                </div>
                <label className="tablee-home-admin__publish-toggle">
                  <span>Publicada</span>
                  <input type="checkbox" checked={imageDraft.isActive !== false} onChange={(event) => updateImageDraft("isActive", event.target.checked)} />
                  <i />
                </label>
              </div>
              <div className="tablee-home-admin__footer-actions">
                <button type="button" className="tablee-carousel__secondary" onClick={() => setIsImageModalOpen(false)}>Cancelar</button>
                <button type="button" onClick={saveSlide}>Guardar sección</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {isSettingsModalOpen ? (
        <div className="tablee-carousel__modal-backdrop" role="presentation" onMouseDown={() => setIsSettingsModalOpen(false)}>
          <form className="tablee-carousel__modal tablee-home-admin__settings-modal" onSubmit={saveHomeSettings} onMouseDown={(event) => event.stopPropagation()}>
            <div className="tablee-carousel__modal-head">
              <div>
                <span>Inicio Tablee</span>
                <h2>Configuracion</h2>
              </div>
              <button type="button" className="tablee-carousel__icon-button" onClick={() => setIsSettingsModalOpen(false)} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="tablee-home-admin__settings-body">
              <label className="tablee-carousel__field">
                <span>Periodo entre vistas</span>
                <div className="tablee-home-admin__interval-control">
                  <button type="button" onClick={() => updateSettingsDraft("slideIntervalSeconds", normalizeIntervalSeconds(Number(settingsDraft.slideIntervalSeconds || 6.5) - 1).toFixed(1))}>
                    <span className="material-symbols-outlined">remove</span>
                  </button>
                  <input
                    type="number"
                    min="2"
                    max="60"
                    step="1"
                    value={settingsDraft.slideIntervalSeconds}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (/^\d*(\.\d?)?$/.test(value)) updateSettingsDraft("slideIntervalSeconds", value);
                    }}
                    onBlur={(event) => updateSettingsDraft("slideIntervalSeconds", normalizeIntervalSeconds(event.target.value).toFixed(1))}
                  />
                  <button type="button" onClick={() => updateSettingsDraft("slideIntervalSeconds", normalizeIntervalSeconds(Number(settingsDraft.slideIntervalSeconds || 6.5) + 1).toFixed(1))}>
                    <span className="material-symbols-outlined">add</span>
                  </button>
                  <strong>segundos</strong>
                </div>
              </label>
              <div className="tablee-carousel__field">
                <span>Transición entre vistas</span>
                <div className="tablee-home-admin__transition-selector" role="group" aria-label="Transición entre vistas">
                  {transitionOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={settingsDraft.slideTransition === option.value ? "is-active" : ""}
                      onClick={() => updateSettingsDraft("slideTransition", option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="tablee-carousel__field">
                <span>Duración de transición</span>
                <div className="tablee-home-admin__interval-control">
                  <button type="button" onClick={() => updateSettingsDraft("slideTransitionSeconds", normalizeTransitionSeconds(Number(settingsDraft.slideTransitionSeconds || 0.5) - 0.1))}>
                    <span className="material-symbols-outlined">remove</span>
                  </button>
                  <input
                    type="number"
                    min="0.1"
                    max="3"
                    step="0.1"
                    value={settingsDraft.slideTransitionSeconds}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (/^\d*(\.\d?)?$/.test(value)) updateSettingsDraft("slideTransitionSeconds", value);
                    }}
                    onBlur={(event) => updateSettingsDraft("slideTransitionSeconds", normalizeTransitionSeconds(event.target.value))}
                  />
                  <button type="button" onClick={() => updateSettingsDraft("slideTransitionSeconds", normalizeTransitionSeconds(Number(settingsDraft.slideTransitionSeconds || 0.5) + 0.1))}>
                    <span className="material-symbols-outlined">add</span>
                  </button>
                  <strong>segundos</strong>
                </div>
              </label>
            </div>
            <div className="tablee-carousel__modal-actions">
              <button type="button" className="tablee-carousel__secondary" onClick={() => setIsSettingsModalOpen(false)}>Cancelar</button>
              <button type="submit">Guardar configuracion</button>
            </div>
          </form>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}

export default TableeHome;
